import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Paper, Alert, CircularProgress,
  MenuItem, Grid, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BrandMark from '../components/BrandMark';
import { getSelfUpdateForm, submitSelfUpdateForm } from '../services/api';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino', label: 'Femenino' },
  { value: 'otro', label: 'Otro' },
];

const emptyForm = {
  first_name: '', last_name: '', phone: '', email: '', address: '',
  dob: '', dni: '', gender: '', marital_status: '',
  blood_type: '', allergies: '', chronic_diseases: '', current_medications: '',
  insurance_provider: '', insurance_policy_number: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
};

// Página pública, sin login — mismo criterio de layout standalone que
// PatientPortal.js. Un link de un solo uso (ver patient_self_update en el
// backend) que recepción genera y comparte por fuera del sistema
// (WhatsApp), para que el paciente cargue o corrija sus propios datos
// antes del turno sin necesitar cuenta. Nunca borra un dato que la
// clínica ya tenía: el backend solo aplica los campos que el paciente
// efectivamente completó acá.
const CompletarDatos = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [isNewPatient, setIsNewPatient] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  // Progressive disclosure: antes los 18 campos estaban siempre visibles de
  // entrada ("choclazo"). Solo lo esencial queda arriba; el resto (datos
  // médicos, contacto de emergencia, etc.) se despliega a pedido — ninguno
  // es obligatorio salvo nombre/apellido para un paciente nuevo, así que
  // ocultarlos no le saca nada al backend, solo reordena la prioridad visual.
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!token) { setLoadError('Este link no es válido.'); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getSelfUpdateForm(token);
        if (cancelled) return;
        const data = res.data;
        setClinicName(data.clinic_name);
        setIsNewPatient(data.is_new_patient);
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          phone: data.phone || '',
          email: data.email || '',
          address: data.address || '',
          dob: data.dob ? data.dob.slice(0, 10) : '',
          dni: data.dni || '',
          gender: data.gender || '',
          marital_status: data.marital_status || '',
          blood_type: data.blood_type || '',
          allergies: data.allergies || '',
          chronic_diseases: data.chronic_diseases || '',
          current_medications: data.current_medications || '',
          insurance_provider: data.insurance_provider || '',
          insurance_policy_number: data.insurance_policy_number || '',
          emergency_contact_name: data.emergency_contact_name || '',
          emergency_contact_phone: data.emergency_contact_phone || '',
          emergency_contact_relationship: data.emergency_contact_relationship || '',
        });
      } catch (err) {
        if (!cancelled) setLoadError(err.userMessage || 'Este link no es válido o ya expiró. Pedí uno nuevo a la clínica.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  // Obligatorios SIEMPRE que se usa este link — regla simple, sin
  // excepciones (pedido explícito del usuario, 2026-10-20): no importa
  // si el paciente es nuevo o ya existía, ni si ya lo había completado
  // antes. A diferencia de la carga por staff en Patients.js, que puede
  // crear un paciente solo con nombre/apellido, acá es la única vía sin
  // supervisión de recepción. Mismo criterio en el backend, ver
  // patient_self_update.py::_require_self_update_fields.
  const REQUIRED_FIELDS = [
    ['first_name', 'Nombre'], ['last_name', 'Apellido'], ['dni', 'DNI'],
    ['email', 'Email'], ['phone', 'Teléfono'],
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED_FIELDS.filter(([field]) => !form[field].trim()).map(([, label]) => label);
    if (missing.length > 0) {
      setSubmitError(`Faltan campos obligatorios: ${missing.join(', ')}.`);
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      await submitSelfUpdateForm(token, form);
      setDone(true);
    } catch (err) {
      setSubmitError(err.userMessage || 'No se pudieron guardar los datos. Probá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const Header = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
      <BrandMark size={28} variant="bare" />
      <Typography variant="h6" fontWeight={800}>Vita<span style={{ opacity: 0.6 }}>Notas</span></Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'background.default', p: 2, pt: 6 }}>
        <Paper sx={{ maxWidth: 420, width: '100%', p: 4, borderRadius: 2 }}>
          <Header />
          <Alert severity="error">{loadError}</Alert>
        </Paper>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'background.default', p: 2, pt: 6 }}>
        <Paper sx={{ maxWidth: 420, width: '100%', p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Header />
          <Alert severity="success" sx={{ mb: 1 }}>¡Listo! Tus datos quedaron guardados.</Alert>
          <Typography variant="body2" color="text.secondary">
            Ya podés avisarle a {clinicName} que completaste el formulario para que confirmen tu turno.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'background.default', p: 2, pt: { xs: 3, sm: 6 } }}>
      <Paper component="form" onSubmit={handleSubmit} sx={{ maxWidth: 560, width: '100%', p: { xs: 2.5, sm: 4 }, borderRadius: 2 }}>
        <Header />
        <Typography variant="h5" fontWeight={700} mb={0.5}>
          {isNewPatient ? 'Completá tus datos' : 'Actualizá tus datos'}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {clinicName} te pidió esta información antes de tu turno. Solo hace falta completar lo que
          quieras agregar o corregir — podés dejar en blanco lo que no sepas ahora.
        </Typography>

        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Apellido *" fullWidth value={form.last_name} onChange={set('last_name')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Nombre *" fullWidth value={form.first_name} onChange={set('first_name')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Teléfono *" fullWidth value={form.phone} onChange={set('phone')} placeholder="Ej: 11 1234-5678" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="DNI *" fullWidth value={form.dni} onChange={set('dni')} />
          </Grid>
          {/* Email y fecha de nacimiento antes vivían detrás del
              "Agregar más datos (opcional)" — en la práctica casi nadie
              abría ese desplegable, así que dos datos importantes
              quedaban sin cargar la mayoría de las veces. Pedido
              explícito del usuario 2026-10-12: subirlos a la sección
              siempre visible. Obra social/N° de afiliado bajan al
              Collapse para no alargar de más esta sección. */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Email *" type="email" fullWidth value={form.email} onChange={set('email')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Fecha de nacimiento" type="date" fullWidth value={form.dob} onChange={set('dob')} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>

        <Button
          onClick={() => setShowMore(p => !p)}
          endIcon={<ExpandMoreIcon sx={{ transform: showMore ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
          sx={{ mt: 1.5, mb: showMore ? 1.5 : 0, textTransform: 'none' }}
        >
          {showMore ? 'Ocultar datos opcionales' : 'Agregar más datos (opcional)'}
        </Button>

        <Collapse in={showMore}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Obra social / prepaga" fullWidth value={form.insurance_provider} onChange={set('insurance_provider')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="N° de afiliado" fullWidth value={form.insurance_policy_number} onChange={set('insurance_policy_number')} />
            </Grid>
            <Grid size={12}>
              <TextField label="Dirección" fullWidth value={form.address} onChange={set('address')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Sexo" fullWidth value={form.gender} onChange={set('gender')}>
                <MenuItem value="">— sin especificar —</MenuItem>
                {GENDERS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Grupo sanguíneo" fullWidth value={form.blood_type} onChange={set('blood_type')}>
                <MenuItem value="">— sin especificar —</MenuItem>
                {BLOOD_TYPES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField label="Alergias" fullWidth multiline rows={2} value={form.allergies} onChange={set('allergies')} />
            </Grid>
            <Grid size={12}>
              <TextField label="Enfermedades crónicas" fullWidth multiline rows={2} value={form.chronic_diseases} onChange={set('chronic_diseases')} />
            </Grid>
            <Grid size={12}>
              <TextField label="Medicación actual" fullWidth multiline rows={2} value={form.current_medications} onChange={set('current_medications')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="Contacto de emergencia" fullWidth value={form.emergency_contact_name} onChange={set('emergency_contact_name')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Teléfono de emergencia" fullWidth value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Parentesco" fullWidth value={form.emergency_contact_relationship} onChange={set('emergency_contact_relationship')} />
            </Grid>
          </Grid>
        </Collapse>

        <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting} sx={{ mt: 3 }}>
          {submitting ? <CircularProgress size={22} /> : 'Guardar mis datos'}
        </Button>
      </Paper>
    </Box>
  );
};

export default CompletarDatos;
